import React, { useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import Modal from "react-bootstrap/Modal";
import { appointmentHasLinkedEncounter } from "../appointments/appointmentEncounter";
import { listAllAvailableSlots } from "../../../api/Scheduling/slots";
import { rescheduleAppointment } from "../../../api/Scheduling/appointments";
import { schedulingErrorMessage } from "../../../api/Scheduling/errors";
import { success } from "../../../common/Toastify";

export default function AppointmentMoveModal({ move, organizationRef: viewOrganizationRef, names, timeZone, onHide, onChanged }) {
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const { appointment, practitionerRef, startsAt, endsAt } = move;
  const organizationRef = appointment.organizationRef || viewOrganizationRef;
  const hasLinkedEncounter = appointmentHasLinkedEncounter(appointment);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setSlot(null);
    setError("");
    listAllAvailableSlots({ organizationRef, serviceCategoryId: appointment.serviceCategoryId,
      practitionerRef, startsAt, endsAt, signal: controller.signal,
      excludeAppointmentId: appointment.appointmentId,
    }).then((slots) => {
      if (!active) return;
      const match = slots.find((item) => moment(item.startsAt).isSame(startsAt) && moment(item.endsAt).isSame(endsAt));
      if (match && (match.scheduleIds?.length || match.scheduleId)) setSlot(match);
      else setError("This time is unavailable for this appointment type. Close and choose another time.");
    }).catch((err) => { if (active) setError(schedulingErrorMessage(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [appointment, organizationRef, practitionerRef, startsAt, endsAt, timeZone]);
  const range = (from, to) => `${moment(from).tz(timeZone).format("ddd, MMM D · h:mm A")}–${moment(to).tz(timeZone).format("h:mm A z")}`;
  const save = async (blockOriginal = false) => {
    if (!slot || inFlight.current || hasLinkedEncounter) return;
    inFlight.current = true;
    setSaving(true);
    setError("");
    try {
      await rescheduleAppointment({ organizationRef, appointmentId: appointment.appointmentId,
        startsAt, endsAt, scheduleIds: slot.scheduleIds || [slot.scheduleId], version: appointment.version,
        ...(blockOriginal ? { blockOriginal: true } : {}) });
      success(blockOriginal ? "Appointment rescheduled and original time blocked" : "Appointment rescheduled");
      onChanged();
    } catch (err) {
      setError(schedulingErrorMessage(err));
      setSlot(null);
    } finally { inFlight.current = false; setSaving(false); }
  };
  return <Modal show centered onHide={saving ? undefined : onHide} backdrop={saving ? "static" : true}
    keyboard={!saving} className="custom-dialog scheduling-dialog" aria-labelledby="appointment-move-title">
    <Modal.Header closeButton={!saving}><Modal.Title id="appointment-move-title">Reschedule appointment</Modal.Title></Modal.Header>
    <Modal.Body>
      <p className="appointment-move-patient">{move.patientName}</p>
      <div className="appointment-move-summary">
        <div><span>From</span><strong>{names[move.fromPractitionerRef] || "Practitioner"}</strong><p>{range(appointment.startsAt, appointment.endsAt)}</p></div>
        <div className="appointment-move-destination"><span>To</span><strong>{names[practitionerRef] || "Practitioner"}</strong><p>{range(startsAt, endsAt)}</p></div>
      </div>
      {loading && <p role="status">Checking availability…</p>}
      {error && <p role="alert" className="text-danger">{error}</p>}
    </Modal.Body>
    <Modal.Footer className="appointment-detail-actions"><button className="btn btn-secondary" disabled={saving} onClick={onHide}>Cancel</button>
      <button className="btn btn-primary" disabled={loading || saving || !slot || hasLinkedEncounter} onClick={() => save(false)}>{saving ? "Rescheduling…" : "Reschedule"}</button>
      <button className="btn btn-secondary" disabled={loading || saving || !slot || hasLinkedEncounter} onClick={() => save(true)}>Block and reschedule</button></Modal.Footer>
  </Modal>;
}
